import "./PdfViewerComponent.scss";
import {ImageConfig} from "../../../constants";
import {Document, Page, pdfjs} from "react-pdf";
import React, {useCallback} from "react";
import IconButtonComponent from "../core/icon-button/IconButtonComponent";
import StatusCardComponent from "../status-card/statusCardComponent";
import ButtonComponent from "../core/button/ButtonComponent";
import LottieFileGenarationComponent from "../core/lottie-file-genaration/LottieFileGenarationComponent";

interface PdfViewerComponentProps {
    title?: string;
    file: any;
    onClose: () => void;
}

const MIN_SCALE = 0.5;
const MAX_SCALE = 3.0;

const PdfViewerComponent = (props: PdfViewerComponentProps) => {
    pdfjs.GlobalWorkerOptions.workerSrc = `//cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfjs.version}/pdf.worker.js`;
    const {file, title, onClose} = props;
    const [totalPages, setTotalPages] = React.useState(0);
    const [pageScale, setPageScale] = React.useState(1);
    const [pageNumber, setPageNumber] = React.useState(1);
    const [isLoading, setIsLoading] = React.useState(true);
    const [isLoadedError, setIsLoadedError] = React.useState(false);

    const onDocumentLoadSuccess = useCallback((e: any) => {
        setIsLoading(false);
        setIsLoadedError(false);
        setTotalPages(e.numPages);
    }, []);

    const onDocumentLoadError = useCallback((e: any) => {
        setIsLoading(false);
        setIsLoadedError(true);
        setTotalPages(e.numPages);
    }, []);

    const handleZoomIn = useCallback(() => {
        if (pageScale < MAX_SCALE) {
            setPageScale(pageScale + 0.2);
        }
    }, [pageScale]);

    const handleZoomOut = useCallback(() => {
        if (pageScale > MIN_SCALE) {
            setPageScale(pageScale - 0.2);
        }
    }, [pageScale]);

    const handleNext = useCallback(() => {
        if (pageNumber < totalPages) {
            setPageNumber(pageNumber + 1);
        }
    }, [pageNumber, totalPages]);

    const handlePrevious = useCallback(() => {
        if (pageNumber > 0) {
            setPageNumber(pageNumber - 1);
        }
    }, [pageNumber]);

    return (
        <div className={'pdf-container'}>
            <div className={'pdf-header'}>
                <div className={'pdf-header-title'}>
                    {title}
                </div>
                <IconButtonComponent className={"pdf-close-btn"} onClick={onClose}>
                    <ImageConfig.CircleCancel/>
                </IconButtonComponent>
            </div>
            <div className="pdf-content-container">
                {
                    isLoading && <div className="h-v-center">
                        <LottieFileGenarationComponent
                            loop={true}
                            autoplay={true}
                            animationData={ImageConfig.LoadingLottieFile}/>
                    </div>
                }
                {
                    isLoadedError && <StatusCardComponent title={"Error loading pdf"}/>
                }
                <Document renderMode={'canvas'} file={file}
                          onLoadSuccess={onDocumentLoadSuccess}
                          onLoadError={onDocumentLoadError}
                          loading={<div className={'h-v-center'}>
                              Loading PDF
                          </div>}
                >
                    <Page pageNumber={pageNumber} scale={pageScale}/>
                </Document>
            </div>
            <div className="pdf-footer">
                <div className="button-container">
                    <ButtonComponent size={"small"} handleClick={handleZoomIn} disabled={pageScale >= 3}>
                        Zoom +
                    </ButtonComponent>&nbsp;&nbsp;
                    <ButtonComponent size={"small"} handleClick={handleZoomOut} disabled={pageScale <= 0.3}>
                        Zoom -
                    </ButtonComponent>
                </div>
                <div className="page-text">
                    Page {pageNumber} of {totalPages}
                </div>
                <div className="button-container">
                    <ButtonComponent size={"small"} handleClick={handlePrevious} disabled={pageNumber === 1}>
                        ‹ Previous
                    </ButtonComponent>&nbsp;&nbsp;
                    <ButtonComponent size={"small"} handleClick={handleNext} disabled={pageNumber === totalPages}>
                        Next ›
                    </ButtonComponent>
                </div>
            </div>
        </div>
    );

};

export default PdfViewerComponent;
